import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { Tasks } from "../lib/api";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { TaskStatusPill } from "../components/StatusPill";
import {
  ArrowRight,
} from "lucide-react";
import SlaBadge from "../components/SlaBadge";
import ExportButton from "../components/ExportButton";
import { defaultFilters, buildApiParams } from "../lib/filters";
import { fmtDate } from "../lib/auditFormat";
import { cn } from "../lib/utils";

const TYPE_META = {
  PDI_VIN:          { label: "VIN Assignment" },
  PDI_READY:        { label: "Vehicle Readiness" },
  ACCOUNTS_INVOICE: { label: "Tax Invoice" },
  INSURANCE:        { label: "Insurance" },
  ROAD_TAX:         { label: "Road Tax" },
  HSRP:             { label: "HSRP" },
  VAS_PP:           { label: "Protection Plus" },
  VAS_AMC:          { label: "AMC" },
  VAS_RSA:          { label: "RSA" },
  SPARES:           { label: "Spares · Accessories" },
};

const ROLE_DEFAULT_TYPE = {
  Accounts: "ACCOUNTS_INVOICE",
  Spares: "SPARES",
  BackOffice: "INSURANCE",
};

const STATUS_TABS = [
  { value: "pending",   label: "Pending" },
  { value: "completed", label: "Completed" },
];

function ToolbarSelect({ value, onChange, placeholder, options, testid, width = "w-[180px]" }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={cn("h-9 text-xs", width)} data-testid={testid}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function TaskQueue() {
  const { activeUser, activeLocationIds, locations, users } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialType = searchParams.get("type") || "all";
  const [type, setType] = useState(initialType);
  const [statusTab, setStatusTab] = useState("pending");
  const [tasks, setTasks] = useState([]);
  const [filters] = useState(defaultFilters());
  const [search, setSearch] = useState("");
  const [locFilter, setLocFilter] = useState("all");
  const locParam = useMemo(() => activeLocationIds.join(","), [activeLocationIds]);
  const locMap = Object.fromEntries(locations.map((l) => [l.id, l]));
  const userMap = Object.fromEntries((users || []).map((u) => [u.id, u]));

  useEffect(() => {
    if (!searchParams.get("type")) {
      const def = ROLE_DEFAULT_TYPE[activeUser?.role];
      if (def) setType(def);
    }
  }, [activeUser, searchParams]);

  useEffect(() => {
    (async () => {
      const params = {
        location_ids: locFilter === "all" ? locParam : locFilter,
        status: statusTab, with_sla: "true",
        ...buildApiParams(filters),
      };
      if (type !== "all") params.type = type;
      const data = await Tasks.list(params);
      setTasks(data);
    })();
  }, [type, statusTab, locParam, locFilter, filters]);

  const filteredTasks = useMemo(() => {
    if (!search.trim()) return tasks;
    const q = search.trim().toLowerCase();
    return tasks.filter((t) =>
      (t.sr_number || "").toLowerCase().includes(q) ||
      (t.customer_name || "").toLowerCase().includes(q) ||
      (t.type || "").toLowerCase().includes(q),
    );
  }, [tasks, search]);

  // Memoised CSV-export params — avoids a fresh object on every render that
  // would otherwise re-trigger ExportButton effects.
  const exportParams = useMemo(() => ({
    ...(type !== "all" ? { type } : {}),
    status: statusTab,
    location_ids: locFilter === "all" ? locParam : locFilter,
    ...buildApiParams(filters),
  }), [type, statusTab, locFilter, locParam, filters]);

  const visibleLocs = locations.filter((l) => activeLocationIds.includes(l.id));

  const setTypeAndUrl = (t) => {
    setType(t);
    if (t === "all") searchParams.delete("type");
    else searchParams.set("type", t);
    setSearchParams(searchParams, { replace: true });
  };

  const resetFilters = () => {
    setType("all"); setStatusTab("pending"); setSearch(""); setLocFilter("all");
    searchParams.delete("type");
    setSearchParams(searchParams, { replace: true });
  };

  return (
    <div className="space-y-3" data-testid="tasks-page">
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">Task Queues</div>
        <h1 className="mt-0.5 font-display text-2xl font-bold tracking-tight text-slate-900">Operations Tasks</h1>
        <p className="mt-0.5 text-xs text-slate-500">
          Filter by task type and status. Click any task to open the sales record.
        </p>
      </div>

      {/* Single toolbar */}
      <Card className="border-slate-200 px-3 py-2">
        <div className="flex flex-wrap items-center gap-2">
          <Input
            placeholder="Search SR / Task type"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 min-w-[240px] flex-[3]"
            data-testid="tasks-search"
          />
          <ToolbarSelect value={locFilter} onChange={setLocFilter}
            placeholder="All Locations"
            options={[
              { value: "all", label: "All Locations" },
              ...visibleLocs.map((l) => ({ value: l.id, label: `${l.code} — ${l.name}` })),
            ]}
            testid="tasks-loc-filter" />
          <ToolbarSelect value={type} onChange={setTypeAndUrl}
            placeholder="All Task Types"
            options={[
              { value: "all", label: "All Task Types" },
              ...Object.entries(TYPE_META).map(([k, m]) => ({ value: k, label: m.label })),
            ]}
            width="w-[170px]"
            testid="tasks-type-filter" />
          <ToolbarSelect value={statusTab} onChange={setStatusTab}
            placeholder="Status"
            options={STATUS_TABS}
            width="w-[130px]"
            testid="tasks-status-filter" />
          <button type="button" onClick={resetFilters}
            className="inline-flex h-9 items-center rounded-md border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600 hover:bg-slate-50"
            data-testid="tasks-reset"
          >
            Reset
          </button>
          <div className="ml-auto">
            <ExportButton
              endpoint="tasks"
              params={exportParams}
              prefix={`Tasks_${type === "all" ? "All" : type}_${statusTab}`}
            />
          </div>
        </div>
      </Card>

      {/* Status tabs (underline) */}
      <div className="border-b border-slate-200">
        <div className="flex flex-wrap items-center gap-1">
          {STATUS_TABS.map((t) => {
            const isActive = statusTab === t.value;
            return (
              <button key={t.value} type="button" onClick={() => setStatusTab(t.value)}
                className={cn(
                  "relative -mb-px px-3.5 py-2 text-sm font-semibold transition-colors",
                  isActive ? "text-blue-600" : "text-slate-500 hover:text-slate-800",
                )}
                data-testid={`task-status-tab-${t.value}`}
              >
                {t.label}
                {isActive && <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-blue-600" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Table */}
      <Card className="border-slate-200 overflow-hidden">
        {filteredTasks.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-slate-500">
            No {statusTab} tasks{type !== "all" ? ` for ${TYPE_META[type]?.label || type}` : ""}.
          </div>
        ) : (
          <div>
            <table className="w-full table-fixed divide-y divide-border text-sm">
              <colgroup>
                <col style={{ width: "26%" }} />
                <col style={{ width: "16%" }} />
                <col style={{ width: "16%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "10%" }} />
                <col />
              </colgroup>
              <thead className="bg-slate-50">
                <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  <th className="px-3 py-2">SR / Customer</th>
                  <th className="px-3 py-2">Task</th>
                  <th className="px-3 py-2">Location</th>
                  <th className="px-3 py-2">DSE</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">SLA</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-white">
                {filteredTasks.map((t) => {
                  const meta = TYPE_META[t.type];
                  const dse = userMap[t.dse_id];
                  return (
                    <tr key={t.id} className="transition-colors hover:bg-slate-50"
                      data-testid={`task-row-${t.id}`}>
                      <td className="px-3 py-1.5">
                        <div className="truncate font-mono-data text-xs font-bold text-slate-900">
                          {t.sr_number || "—"}
                        </div>
                        <div className="mt-0.5 truncate text-[11px] font-medium text-slate-700"
                             title={t.customer_name || ""}
                             data-testid={`task-customer-${t.id}`}>
                          {t.customer_name || "—"}
                        </div>
                        <div className="mt-0.5 truncate font-mono-data text-[10px] text-slate-500">
                          {fmtDate(t.created_at)}
                        </div>
                      </td>
                      <td className="px-3 py-1.5">
                        <div className="truncate text-xs font-semibold text-slate-900">
                          {meta?.label || t.type}
                        </div>
                      </td>
                      <td className="px-3 py-1.5 font-mono-data text-[11px] text-slate-700">
                        {locMap[t.location_id]?.code || "—"}
                      </td>
                      <td className="px-3 py-1.5">
                        <span className="truncate text-xs font-medium text-slate-800"
                              data-testid={`task-dse-${t.id}`}>
                          {dse?.name || "—"}
                        </span>
                      </td>
                      <td className="px-3 py-1.5">
                        <TaskStatusPill status={t.status} />
                      </td>
                      <td className="px-3 py-1.5">
                        <SlaBadge task={t} />
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        <Link to={`/sales/${t.sale_id}`}
                          className="inline-flex items-center gap-0.5 rounded-md bg-primary/5 px-2 py-1 text-[11px] font-semibold text-primary hover:bg-primary/10"
                          data-testid={`task-open-${t.id}`}>
                          Open <ArrowRight className="h-3 w-3" />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
